/**
 * Trang chi tiết BĐS — hiển thị thông tin đầy đủ một tin đăng.
 *
 * Luồng dữ liệu:
 * - Lấy id từ URL params → usePropertyDetails fetch chi tiết tin.
 * - Gọi propertyApi.getSimilarProperties để lấy tin tương tự.
 * - Derive các nhãn hiển thị (spec, features, bản đồ) từ selectedProperty.
 * - Sidebar/desktop: PropertyBookingCard; mobile: bottom bar + sheet liên hệ.
 */
import { Link, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { EmptyState } from '../../components/Common/EmptyState';
import { ShareButton } from '../../components/Common/ShareButton';
import { FavoriteButton } from '../../components/property/FavoriteButton';
import { PropertyBookingCard } from '../../components/property/PropertyBookingCard';
import { PropertyDetailSkeleton } from '../../components/property/PropertyDetailSkeleton';
import { PropertyGallery } from '../../components/property/PropertyGallery';
import { ListingRow } from '../../components/home/ListingRow';
import { propertyApi } from '../../features/properties/api/propertyApi';
import { usePropertyDetails } from '../../features/properties/hooks/usePropertyDetails';
import {
  FURNISHED_STATUS_OPTIONS,
  getTransactionLabel,
  PROPERTY_FEATURE_OPTIONS,
} from '../../shared/constants/property';
import { APP_ROUTES } from '../../shared/constants/routes';
import { TEXT } from '../../shared/languages';
import { formatCompactCount } from '../../shared/utils/formatCompactCount';
import { formatPropertyPrice } from '../../shared/utils/formatCurrency';
import { scrollToPageTop } from '../../shared/utils/scrollToPageTop';

/** Số ký tự mô tả hiển thị trước khi cần nút "Xem thêm" */
const DESCRIPTION_PREVIEW_LENGTH = 320;

/** Tạo URL embed Google Maps từ nhãn khu vực */
const buildAreaMapEmbedUrl = (areaLabel) => {
  if (!areaLabel) {
    return '';
  }

  const mapQuery = encodeURIComponent(areaLabel);

  return `https://www.google.com/maps?q=${mapQuery}&output=embed`;
};

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BedIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M3 10V8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2M3 14v4M21 14v4M3 14h18M7 10V7M17 10V7" strokeLinecap="round" />
  </svg>
);

const AreaIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <path d="M4 9h16M9 4v16" />
  </svg>
);

const ViewIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

/** Map mã trạng thái nội thất sang nhãn hiển thị */
const getFurnishedLabel = (status) =>
  FURNISHED_STATUS_OPTIONS.find((option) => option.value === status)?.label || status;

export const PropertyDetailPage = () => {
  const { id } = useParams();
  const { selectedProperty, isLoadingProperty } = usePropertyDetails(id);

  /** Danh sách tin tương tự — fetch riêng, không qua hook chính */
  const [similarProperties, setSimilarProperties] = useState([]);
  /** Điều khiển sheet liên hệ trên mobile */
  const [showMobileContact, setShowMobileContact] = useState(false);
  /** Trạng thái mở rộng/thu gọn mô tả dài */
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const t = TEXT.property.detail;
  const common = TEXT.common;
  const transaction = TEXT.property.transactionType;

  /** Cuộn lên đầu khi chuyển sang tin khác */
  useEffect(() => {
    scrollToPageTop();
  }, [id]);

  /** Tải danh sách BĐS tương tự khi id thay đổi */
  useEffect(() => {
    if (!id) {
      return undefined;
    }

    propertyApi
      .getSimilarProperties(id)
      .then((response) => setSimilarProperties(response.data.data || []))
      .catch(() => setSimilarProperties([]));
  }, [id]);

  /** Ghép dòng thông số phòng (phòng ngủ, WC, diện tích...) */
  const specLine = useMemo(() => {
    if (!selectedProperty) {
      return '';
    }

    const parts = [];

    if (selectedProperty.bedrooms !== null && selectedProperty.bedrooms !== undefined) {
      parts.push(`${selectedProperty.bedrooms} ${t.bedroom}`);
    }

    if (selectedProperty.bathrooms !== null && selectedProperty.bathrooms !== undefined) {
      parts.push(`${selectedProperty.bathrooms} ${t.bathroom}`);
    }

    if (selectedProperty.toilets !== null && selectedProperty.toilets !== undefined) {
      parts.push(`${selectedProperty.toilets} toilet`);
    }

    if (selectedProperty.kitchens !== null && selectedProperty.kitchens !== undefined) {
      parts.push(`${selectedProperty.kitchens} bếp`);
    }

    if (selectedProperty.area) {
      parts.push(`${selectedProperty.area} ${common.areaUnit}`);
    }

    return parts.join(' · ');
  }, [common.areaUnit, selectedProperty, t]);

  /** Danh sách tiện ích và thông tin pháp lý để render checklist */
  const featureItems = useMemo(() => {
    if (!selectedProperty) {
      return [];
    }

    const items = [];

    if (selectedProperty.furnished_status) {
      items.push(`${t.furnished}: ${getFurnishedLabel(selectedProperty.furnished_status)}`);
    }

    if (selectedProperty.legal_status) {
      items.push(`${t.legal}: ${selectedProperty.legal_status}`);
    }

    PROPERTY_FEATURE_OPTIONS.filter(
      (featureOption) => selectedProperty.features?.[featureOption.resourceKey]
    ).forEach((featureOption) => {
      items.push(featureOption.label);
    });

    return items;
  }, [selectedProperty, t]);

  if (isLoadingProperty) {
    return <PropertyDetailSkeleton />;
  }

  if (!selectedProperty) {
    return (
      <EmptyState
        title={t.notFoundTitle}
        message={t.notFoundMessage}
        action={<Link to={APP_ROUTES.home} className="button">{t.backToList}</Link>}
      />
    );
  }

  const propertyUrl = `${window.location.origin}${APP_ROUTES.propertyDetails(selectedProperty.id)}`;
  const categoryName = selectedProperty.category?.name || TEXT.property.defaultCategory;
  const locationLabel = [selectedProperty.ward, selectedProperty.province].filter(Boolean).join(', ');
  const consultationArea = locationLabel || selectedProperty.location || selectedProperty.province || common.notUpdated;
  const areaMapUrl = buildAreaMapEmbedUrl(consultationArea);
  const transactionLabel = getTransactionLabel(selectedProperty.transaction_type, {
    sale: transaction.saleDetail,
    rent: transaction.rent,
  });
  const introLine = [categoryName, transactionLabel, locationLabel].filter(Boolean).join(' · ');
  const hostName = selectedProperty.user?.name || t.defaultHost;
  const hostInitial = hostName.trim().charAt(0).toUpperCase() || 'N';
  const descriptionText = selectedProperty.description || t.noDescription;
  const shouldTruncateDescription =
    descriptionText.length > DESCRIPTION_PREVIEW_LENGTH && !isDescriptionExpanded;
  const visibleDescription = shouldTruncateDescription
    ? `${descriptionText.slice(0, DESCRIPTION_PREVIEW_LENGTH).trim()}…`
    : descriptionText;
  const viewsCount = selectedProperty.views_count ?? selectedProperty.views ?? 0;

  /** Ba ô highlight chính: phòng, diện tích, lượt xem */
  const highlights = [
    {
      key: 'rooms',
      icon: <BedIcon />,
      title:
        selectedProperty.bedrooms != null
          ? `${selectedProperty.bedrooms} ${t.bedroom}`
          : categoryName,
      subtitle:
        selectedProperty.toilets != null
          ? `${selectedProperty.toilets} toilet`
          : selectedProperty.kitchens != null
            ? `${selectedProperty.kitchens} bếp`
            : selectedProperty.bathrooms != null
              ? `${selectedProperty.bathrooms} ${t.bathroom}`
          : transactionLabel,
    },
    {
      key: 'area',
      icon: <AreaIcon />,
      title: selectedProperty.area ? `${selectedProperty.area} ${common.areaUnit}` : t.areaLabel,
      subtitle: selectedProperty.floors ? `${selectedProperty.floors} ${t.floor}` : categoryName,
    },
    {
      key: 'views',
      icon: <ViewIcon />,
      title: t.viewsLabel.replace('{count}', formatCompactCount(viewsCount)),
      subtitle: selectedProperty.legal_status || transactionLabel,
    },
  ];

  return (
    <>
      <section className="property-detail-page">
        <div className="property-detail-container">
          <header className="property-detail-top">
            <h1>{selectedProperty.title}</h1>
            <div className="property-detail-top-actions">
              <ShareButton title={selectedProperty.title} url={propertyUrl} variant="detail" />
              <FavoriteButton propertyId={selectedProperty.id} variant="detail" />
            </div>
          </header>

          <PropertyGallery images={selectedProperty.images} />

          <div className="property-detail-body">
            <div className="property-detail-main">
              <div className="property-detail-intro">
                <p className="property-detail-type">{introLine}</p>
                {specLine && <p className="property-detail-specs">{specLine}</p>}
              </div>

              <hr className="property-detail-divider" />

              <div className="property-detail-host-row">
                <div className="property-detail-host-copy">
                  <h2>{t.hostedBy.replace('{name}', hostName)}</h2>
                  <p>{t.hostSubtitle.replace('{category}', categoryName).replace('{location}', locationLabel || '—')}</p>
                </div>
                <div className="property-detail-host-avatar" aria-hidden="true">
                  {hostInitial}
                </div>
              </div>

              <div className="property-detail-highlights">
                {highlights.map((item) => (
                  <div key={item.key} className="property-detail-highlight">
                    <span className="property-detail-highlight-icon">{item.icon}</span>
                    <div>
                      <strong>{item.title}</strong>
                      <span>{item.subtitle}</span>
                    </div>
                  </div>
                ))}
              </div>

              <hr className="property-detail-divider" />

              <section className="property-detail-section">
                <h2 className="property-detail-section-title">{common.description}</h2>
                <p className="property-detail-description">{visibleDescription}</p>
                {descriptionText.length > DESCRIPTION_PREVIEW_LENGTH && (
                  <button
                    type="button"
                    className="property-detail-description-toggle"
                    onClick={() => setIsDescriptionExpanded((expanded) => !expanded)}
                  >
                    {isDescriptionExpanded ? t.showLess : t.showMore}
                  </button>
                )}
              </section>

              {featureItems.length > 0 && (
                <section className="property-detail-section">
                  <h2 className="property-detail-section-title">{common.amenities}</h2>
                  <ul className="property-detail-amenities">
                    {featureItems.map((label) => (
                      <li key={label}>
                        <CheckIcon />
                        {label}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section className="property-detail-section">
                <h2 className="property-detail-section-title">{t.areaMapTitle}</h2>
                <div className="property-detail-area-map-card">
                  <div className="property-detail-area-map-copy">
                    <strong>{consultationArea}</strong>
                    <p>{t.areaMapHint}</p>
                  </div>
                  {areaMapUrl && (
                    <iframe
                      className="property-detail-area-map-frame"
                      src={areaMapUrl}
                      title={t.areaMapTitle}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  )}
                </div>
              </section>
            </div>

            <aside className="property-detail-sidebar">
              <div className="property-detail-sidebar-sticky">
                <PropertyBookingCard
                  property={selectedProperty}
                  price={selectedProperty.price}
                  transactionType={selectedProperty.transaction_type}
                />
              </div>
            </aside>
          </div>
        </div>

        {similarProperties.length > 0 && (
          <ListingRow
            title={t.similarTitle}
            properties={similarProperties}
            className="listing-row--detail-page"
          />
        )}
      </section>

      <div className="property-detail-mobile-bar">
        <div>
          <strong>{formatPropertyPrice(selectedProperty.price)}</strong>
          {selectedProperty.transaction_type === 'rent' && (
            <span>{t.perMonth}</span>
          )}
        </div>
        <button type="button" className="property-detail-mobile-cta" onClick={() => setShowMobileContact(true)}>
          {t.contactCta}
        </button>
      </div>

      {showMobileContact && (
        <div className="property-detail-mobile-sheet" role="dialog" aria-modal="true">
          <button
            type="button"
            className="property-detail-mobile-sheet-backdrop"
            aria-label={common.close}
            onClick={() => setShowMobileContact(false)}
          />
          <div className="property-detail-mobile-sheet-panel">
            <PropertyBookingCard
              property={selectedProperty}
              price={selectedProperty.price}
              transactionType={selectedProperty.transaction_type}
            />
          </div>
        </div>
      )}
    </>
  );
};
